// HR letters (Phase 2a) — role-adaptive ESS screen. Employees request letters
// and print issued ones; HR previews, edits and issues (or rejects).
import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { Plus, Printer, FileText } from 'lucide-react';
import { Card, SectionTitle, CenterSpinner, PrimaryBtn, OutlineBtn, Pill } from './ui';
import { EmptyState } from './components';
import { AG } from './theme/tokens';
import { api } from './api';
import { useAuth } from './auth';

const TYPES = ['Employment', 'Experience', 'Salary', 'Bank', 'Visa'];
const tone = (s) => (s === 'Issued' ? 'success' : s === 'Rejected' ? 'error' : 'warning');

function printLetter(body) {
  const w = window.open('', '_blank', 'width=720,height=900');
  if (!w) return;
  w.document.write(`<pre style="font-family:Georgia,serif;font-size:14px;line-height:1.6;white-space:pre-wrap;padding:48px;max-width:680px;margin:auto">${(body || '').replace(/</g, '&lt;')}</pre>`);
  w.document.close(); w.focus(); w.print();
}

export default function Letters({ showToast }) {
  const { isHR } = useAuth();
  return isHR ? <LettersHR showToast={showToast} /> : <LettersEmployee showToast={showToast} />;
}

// ── Employee: request + print own letters ───────────────────────────────────
function LettersEmployee({ showToast }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.myLetters().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (busy) return; setBusy(true);
    try { await api.requestLetter({ type: form.type, addressee: form.addressee, purpose: form.purpose }); showToast?.('Letter requested'); setForm(null); await load(); }
    catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  const view = async (r) => { try { const full = await api.letter(r.id); printLetter(full.body); } catch (e) { showToast?.(e.message); } };

  if (!rows) return <CenterSpinner />;
  return (
    <div className="space-y-4 fade-up">
      <div className="flex items-center justify-between" style={{ gap: 12, flexWrap: 'wrap' }}>
        <SectionTitle title="Letters" sub="Request HR letters and print issued ones" />
        <PrimaryBtn onClick={() => setForm({ type: 'Employment', addressee: '', purpose: '' })}><Plus size={14} style={{ marginRight: 6 }} /> Request a letter</PrimaryBtn>
      </div>
      {rows.length === 0 ? (
        <Card><EmptyState icon={FileText} title="No letters yet" desc="Request an employment, salary, bank or visa letter." /></Card>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {rows.map((r) => (
            <Card key={r.id} style={{ padding: 14 }} className="flex items-center justify-between">
              <div>
                <div style={{ fontWeight: 600, color: AG.text }}>{r.type} letter {r.addressee ? <span style={{ color: AG.n400, fontWeight: 400 }}>· {r.addressee}</span> : null}</div>
                <div style={{ fontSize: 12, color: AG.n500 }}>{r.createdAt?.slice(0, 10)}{r.purpose ? ` · ${r.purpose}` : ''}</div>
              </div>
              <div className="flex items-center gap-2">
                <Pill tone={tone(r.status)}>{r.status}</Pill>
                {r.status === 'Issued' && <OutlineBtn onClick={() => view(r)}><Printer size={14} style={{ marginRight: 6 }} /> Print</OutlineBtn>}
              </div>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Request a letter</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Type</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="ag-input" style={{ marginTop: 4, marginBottom: 12 }}>
                {TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
              <TextField fullWidth size="small" label="Addressee (bank / embassy)" value={form.addressee} onChange={(e) => setForm({ ...form, addressee: e.target.value })} sx={{ mb: 1.5 }} />
              <TextField fullWidth size="small" label="Purpose (optional)" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} />
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn>
          <PrimaryBtn onClick={submit} disabled={busy}>Request</PrimaryBtn>
        </DialogActions>
      </Dialog>
    </div>
  );
}

// ── HR: issue / reject letter requests ───────────────────────────────────────
function LettersHR({ showToast }) {
  const [rows, setRows] = useState(null);
  const [issue, setIssue] = useState(null); // { id, type, employeeName, body }
  const [busy, setBusy] = useState(false);
  const load = () => api.lettersAll().then(setRows).catch((e) => { showToast?.(e.message); setRows([]); });
  useEffect(() => { load(); }, []);

  const openIssue = async (r) => {
    try { const p = await api.previewLetter(r.id); setIssue({ id: r.id, type: r.type, employeeName: r.employeeName, body: p.body }); }
    catch (e) { showToast?.(e.message); }
  };
  const doIssue = async () => { if (busy) return; setBusy(true); try { await api.issueLetter(issue.id, issue.body); showToast?.('Letter issued'); setIssue(null); await load(); } catch (e) { showToast?.(e.message); } finally { setBusy(false); } };
  const reject = async (r) => { try { await api.rejectLetter(r.id); await load(); } catch (e) { showToast?.(e.message); } };
  const view = async (r) => { try { const full = await api.letter(r.id); printLetter(full.body); } catch (e) { showToast?.(e.message); } };

  if (!rows) return <CenterSpinner />;
  return (
    <div className="space-y-4 fade-up">
      <SectionTitle title="Letters" sub="Issue employment, salary, bank and visa letters" />
      {rows.length === 0 ? (
        <Card><EmptyState icon={FileText} title="No letter requests" desc="Requests from employees appear here to issue." /></Card>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {rows.map((r) => (
            <Card key={r.id} style={{ padding: 14 }} className="flex items-center justify-between">
              <div>
                <div style={{ fontWeight: 600, color: AG.text }}>{r.employeeName} <span style={{ color: AG.n400, fontWeight: 400 }}>· {r.type}{r.addressee ? ` · ${r.addressee}` : ''}</span></div>
                <div style={{ fontSize: 12, color: AG.n500 }}>{r.dept} · {r.createdAt?.slice(0, 10)}{r.purpose ? ` · ${r.purpose}` : ''}</div>
              </div>
              <div className="flex items-center gap-2">
                <Pill tone={tone(r.status)}>{r.status}</Pill>
                {r.status === 'Pending' && <><OutlineBtn onClick={() => reject(r)}>Reject</OutlineBtn><PrimaryBtn onClick={() => openIssue(r)}>Issue</PrimaryBtn></>}
                {r.status === 'Issued' && <OutlineBtn onClick={() => view(r)}><Printer size={14} style={{ marginRight: 6 }} /> Print</OutlineBtn>}
              </div>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={!!issue} onClose={() => !busy && setIssue(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Issue {issue?.type} letter · {issue?.employeeName}</DialogTitle>
        <DialogContent>
          {issue && <TextField fullWidth multiline minRows={12} value={issue.body} onChange={(e) => setIssue({ ...issue, body: e.target.value })} sx={{ mt: 1, '& textarea': { fontFamily: 'Georgia, serif', fontSize: 13, lineHeight: 1.6 } }} />}
        </DialogContent>
        <DialogActions>
          <OutlineBtn onClick={() => setIssue(null)} disabled={busy}>Cancel</OutlineBtn>
          <PrimaryBtn onClick={doIssue} disabled={busy}>Issue letter</PrimaryBtn>
        </DialogActions>
      </Dialog>
    </div>
  );
}
